/** 手机布局：工具栏允许换行，手写弹窗限制动态可视高度，避免窄屏和键盘弹出后遮住业务操作。 */
import { ResponsiveTable } from "../mobile/ResponsiveTable";
/** 手机记录列表复用原有数据、勾选与业务操作；普通记录按字段卡片呈现，交互表头和对比矩阵保留横向浏览。 */
import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { FileDown, FileSpreadsheet, Upload, Loader2 } from "lucide-react";
import { PoolImportHistory } from "./PoolImportHistory";

const dl = (blob, name) => { const u = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = u; a.download = name; a.click(); URL.revokeObjectURL(u); };
const STATUS = { new: { zh: "新增", en: "New", cls: "bg-emerald-100 text-emerald-700" }, dup: { zh: "重复", en: "Duplicate", cls: "bg-amber-100 text-amber-700" }, invalid: { zh: "无效", en: "Invalid", cls: "bg-red-100 text-red-700" } };
const BR = { la: "洛杉矶 LA", norcal: "北加州 NorCal", sd: "圣地亚哥 SD" };
const BSRC = { zip: "按邮编", city: "按城市", default: "默认" };

/* 客户公海 Excel 导出 / 模板 / 导入（仅 admin / CEO 渲染） */
export function PoolImportExport({ api, zh, onImported }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [prev, setPrev] = useState(null);
  const [fill, setFill] = useState(false);

  const download = async (path, name, msg) => {
    setBusy(true);
    try { const res = await api.get(path, { responseType: "blob" }); dl(res.data, name); toast.success(msg(res.headers["x-rows"])); }
    catch { toast.error(zh ? "下载失败" : "Download failed"); }
    setBusy(false);
  };
  const preview = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/crm/pool/import?apply=0", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPrev({ ...data, file }); setFill(false);
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "解析失败" : "Parse failed")); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const apply = async () => {
    if (!prev?.file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", prev.file);
      const { data } = await api.post(`/crm/pool/import?apply=1&fill=${fill ? 1 : 0}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(zh ? `已导入 ${data.created} 个新客户到公海${data.filled ? `，补全 ${data.filled} 个已有客户` : ""}；跳过重复 ${data.dup} · 无效 ${data.invalid}` : `Imported ${data.created}, filled ${data.filled}, skipped ${data.dup} dup / ${data.invalid} invalid`);
      setPrev(null); onImported?.();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "导入失败" : "Import failed")); }
    setBusy(false);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="pool-io-bar">
      <Button variant="outline" size="sm" data-testid="pool-export" disabled={busy} onClick={() => download("/crm/pool/export.xlsx", `crm_pool_${new Date().toISOString().slice(0, 10)}.xlsx`, (n) => (zh ? `已导出 ${n || ""} 条公海客户` : `Exported ${n || ""} rows`))}>
        <FileDown className="h-4 w-4 mr-1" />{zh ? "导出 Excel" : "Export Excel"}
      </Button>
      <Button variant="outline" size="sm" data-testid="pool-template" disabled={busy} onClick={() => download("/crm/pool/template.xlsx", "crm_pool_import_template.xlsx", () => (zh ? "已下载导入模板" : "Template downloaded"))}>
        <FileSpreadsheet className="h-4 w-4 mr-1" />{zh ? "下载导入模板" : "Template"}
      </Button>
      <input ref={fileRef} type="file" accept=".xlsx" className="hidden" data-testid="pool-import-file" onChange={(e) => preview(e.target.files?.[0])} />
      <Button size="sm" data-testid="pool-import-btn" disabled={busy} onClick={() => fileRef.current?.click()} className="bg-slate-900 hover:bg-slate-800 text-white">
        {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}{zh ? "导入 Excel" : "Import Excel"}
      </Button>
      <PoolImportHistory api={api} zh={zh} onChanged={onImported} />

      {prev && (
        <Dialog open onOpenChange={(o) => !o && setPrev(null)}>
          <DialogContent className="max-w-4xl" data-testid="pool-import-dialog">
            <DialogHeader>
              <DialogTitle>{zh ? "导入预览 · 客户公海" : "Import preview · Pool"}</DialogTitle>
              <DialogDescription>{prev.filename} · {zh ? `共 ${prev.rows} 行；按电话后 10 位与系统内全部客户去重` : `${prev.rows} rows; dedupe by last 10 digits of phone`}</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-3 gap-2" data-testid="pool-import-counts">
              {[["new", prev.new], ["dup", prev.dup], ["invalid", prev.invalid]].map(([k, n]) => (
                <div key={k} className={`rounded-lg px-3 py-2 ${STATUS[k].cls}`} data-testid={`pool-import-count-${k}`} data-count={n}>
                  <p className="text-[11px] opacity-80">{zh ? STATUS[k].zh : STATUS[k].en}{k === "dup" && prev.dupSystem != null ? (zh ? `（系统已有 ${prev.dupSystem}）` : ` (${prev.dupSystem} in system)`) : ""}</p>
                  <p className="text-xl font-bold leading-tight">{n}</p>
                </div>
              ))}
            </div>
            {prev.branchAuto > 0 && <p className="text-xs text-slate-500" data-testid="pool-import-branch-auto">{zh ? `${prev.branchAuto} 条未填分公司，已按邮编 / 城市自动归类，无法判断的归洛杉矶` : `${prev.branchAuto} rows auto-classified by ZIP / city (fallback: LA)`}</p>}
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid="pool-import-fill">
              <input type="checkbox" checked={fill} onChange={(e) => setFill(e.target.checked)} disabled={!prev.dupSystem} />
              {zh ? "重复客户：用 Excel 中的邮箱 / 地址 / 城市 / 邮编 / 备注补全系统内空字段（不覆盖已有值）" : "Duplicates: fill empty fields of existing leads (never overwrite)"}
            </label>
            <div className="max-h-[45vh] overflow-auto border border-slate-200 rounded-lg">
              <ResponsiveTable className="w-full min-w-[560px] text-xs whitespace-nowrap">
                <thead className="bg-slate-50 text-slate-500 sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left">#</th><th className="px-2 py-1.5 text-left">{zh ? "状态" : "Status"}</th><th className="px-2 py-1.5 text-left">{zh ? "客户" : "Customer"}</th>
                    <th className="px-2 py-1.5 text-left">{zh ? "电话" : "Phone"}</th><th className="px-2 py-1.5 text-left">{zh ? "分公司" : "Branch"}</th><th className="px-2 py-1.5 text-left">{zh ? "城市" : "City"}</th><th className="px-2 py-1.5 text-left">{zh ? "说明" : "Note"}</th>
                  </tr>
                </thead>
                <tbody>
                  {(prev.preview || []).map((r) => (
                    <tr key={r.row} className="border-t border-slate-100" data-testid={`pool-import-row-${r.row}`} data-status={r.status}>
                      <td className="px-2 py-1 text-slate-400">{r.row}</td>
                      <td className="px-2 py-1"><span className={`rounded-full px-1.5 py-0.5 font-semibold ${STATUS[r.status].cls}`}>{zh ? STATUS[r.status].zh : STATUS[r.status].en}</span></td>
                      <td className="px-2 py-1 font-medium text-slate-800">{r.clientName || <span className="text-slate-300">—</span>}</td>
                      <td className="px-2 py-1 font-mono">{r.phone}</td>
                      <td className="px-2 py-1">{BR[r.branch] || r.branch}{r.branchSource !== "column" && <span className="ml-1 text-[10px] text-slate-400">({BSRC[r.branchSource] || r.branchSource})</span>}</td>
                      <td className="px-2 py-1 text-slate-500">{r.city}</td>
                      <td className="px-2 py-1 text-slate-500">{r.reason || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </ResponsiveTable>
            </div>
            {prev.rows > 50 && <p className="text-[11px] text-slate-400">{zh ? `仅显示前 50 行，确认后将处理全部 ${prev.rows} 行` : `Showing first 50 of ${prev.rows} rows`}</p>}
            <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
              <Button variant="outline" size="sm" data-testid="pool-import-cancel" onClick={() => setPrev(null)}>{zh ? "取消" : "Cancel"}</Button>
              <Button size="sm" data-testid="pool-import-confirm" disabled={busy || (prev.new === 0 && !(fill && prev.dupSystem))} onClick={apply} className="bg-cyan-600 hover:bg-cyan-700 text-white">
                {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}{zh ? `确认导入 ${prev.new} 个新客户${fill && prev.dupSystem ? ` + 补全 ${prev.dupSystem}` : ""}` : `Import ${prev.new} new${fill && prev.dupSystem ? ` + fill ${prev.dupSystem}` : ""}`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
